import { GetServerSideProps } from "next";
import { Box, Container, Grid, Stack } from "@mui/material";
import React from "react";
import { SWAFPrompt } from "src/components/reusable/components/SWAFPrompt";
import { UpcomingEvents } from "src/components/feature/PurchaseFlow/UpcomingEvents";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { PageLayout } from "src/components/reusable/components/PageLayout";
import { EmptyState } from "src/components/base/components/EmptyState";
import { DynamicSkeleton } from "src/components/base/components/DynamicSkeleton";
import { PassBanner } from "src/components/feature/PurchaseFlow/PassBanner";
import { PassCardList } from "src/components/feature/PurchaseFlow/PassCardList";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { RegistrationCardContainer } from "src/components/feature/PurchaseFlow/RegistrationCardContainer";
import { StaticBanner } from "src/components/feature/PurchaseFlow/StaticBanner";
import { usePrePurchaseFAQModal } from "src/components/reusable/components/GlobalModals/PrePurchaseFAQModal";
import { usePassFlow } from "src/components/feature/PurchaseFlow/usePassFlow";
import { PassGroupCardContainer } from "src/components/feature/common/PassGroupCardContainer";
import { getUpcomingEventsToDisplay } from "src/utils/get-upcoming-events-to-display";
import { StaticBannerRegistrationCard } from "src/components/feature/PurchaseFlow/StaticBannerRegistrationCard";
import type { TMLBBallparkUser } from "src/shared/types/globalStotre.types";
import { constants } from "src/shared/constants";

export default function LandingPage({
  mlbBallparkOktaId,
  mlbBallparkEmail,
}: TMLBBallparkUser) {
  const teamConfig = useTeamConfig();
  const {
    LANDING_PAGE_SETTINGS,
    IMAGES,
    TEAM_FULL_NAME,
    TEAM_GLOBAL_SETTINGS,
    LANGUAGES,
  } = teamConfig;
  const { sm: smDown } = useCssMediaQuery("down");
  const { passes, user, setGlobalState } = useGlobalStore();
  const {
    handleGetPassesUrlCode,
    handleGetPasses,
    handleGetPassesOnAccount,
    onClickPassGroupPurchase,
  } = usePassFlow();
  const [isLoading, setLoading] = React.useState(true);
  const router = useRouterExt();
  const urlCode = router?.query?.urlCode;

  const prePurchaseFAQModal = usePrePurchaseFAQModal();
  function onClickFAQButton(passId: number) {
    prePurchaseFAQModal.set({
      isOpen: true,
      faqs: LANGUAGES?.PRE_PURCHASE_FAQS_BY_PASS_ID
        ? LANGUAGES?.PRE_PURCHASE_FAQS_BY_PASS_ID?.[passId]
        : LANGUAGES?.PRE_PURCHASE_FAQS || LANGUAGES?.FAQS,
    });
  }

  React.useEffect(() => {
    (async () => {
      if (urlCode) await handleGetPassesUrlCode(urlCode as string);
      else await handleGetPasses();
      user && (await handleGetPassesOnAccount());
      setLoading(false);
    })();
  }, []);

  React.useEffect(() => {
    if (mlbBallparkOktaId) {
      setGlobalState({ mlbBallparkUser: { mlbBallparkOktaId, mlbBallparkEmail } });
    }
  }, []);

  return (
    <PageLayout>
      <Stack spacing={4} alignItems={"center"}>
        {LANDING_PAGE_SETTINGS?.STATIC_BANNER?.IS_ENABLED &&
        IMAGES?.LANDING_PAGE_STATIC_BANNER_URL ? (
          <Box width={"100%"}>
            {LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS
              ?.DISPLAY_ABOVE_STATIC_BANNER &&
            LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS
              ?.REGISTRATION_CARDS?.length &&
            smDown ? (
              <StaticBannerRegistrationCard />
            ) : (
              <StaticBanner
                src={
                  smDown && IMAGES?.MOBILE_LANDING_PAGE_STATIC_BANNER_URL
                    ? IMAGES?.MOBILE_LANDING_PAGE_STATIC_BANNER_URL
                    : IMAGES?.LANDING_PAGE_STATIC_BANNER_URL
                }
                alt={`${TEAM_FULL_NAME} Image`}
                isFluidImageSize={
                  LANDING_PAGE_SETTINGS?.STATIC_BANNER
                    ?.IS_FLUID_BACKGROUND_IMAGE_SIZE
                }
                mobileMaxHeight={
                  LANDING_PAGE_SETTINGS?.PASS_BANNER?.MOBILE_MAX_HEIGHT
                }
                maxHeight={
                  LANDING_PAGE_SETTINGS?.STATIC_BANNER?.MAX_HEIGHT
                }
              />
            )}
          </Box>
        ) : null}

        {TEAM_GLOBAL_SETTINGS?.IS_PASS_GROUP_ENABLED ? (
          <Box width={"100%"}>
            <Container maxWidth={"lg"}>
              <PassGroupCardContainer
                cardProps={{
                  onClickPurchase: onClickPassGroupPurchase,
                }}
              />
            </Container>
          </Box>
        ) : null}

        {LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS?.REGISTRATION_CARDS
          ?.length &&
        (!LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS
          ?.DISPLAY_ABOVE_STATIC_BANNER ||
          !smDown) ? (
          <Box width={"100%"}>
            <Container maxWidth={"lg"}>
              <RegistrationCardContainer
                title={
                  LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS
                    ?.SECTION_TITLE
                }
                hideTitle={
                  LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS
                    ?.HIDE_SECTION_TITLE
                }
                cards={
                  LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS
                    ?.REGISTRATION_CARDS
                }
                isExpandedCard={
                  LANDING_PAGE_SETTINGS?.REGISTRATION_CARD_SETTINGS
                    ?.IS_EXPANDED_CARD
                }
              />
            </Container>
          </Box>
        ) : null}

        {isLoading ? (
          <Box width={"100%"}>
            <Container maxWidth={"lg"}>
              <Grid container spacing={2} py={4}>
                {Array.from({ length: 4 })
                  .fill(1)
                  .map((itm, idx) => {
                    return (
                      <Grid item key={idx} xs={12}>
                        <DynamicSkeleton
                          rowCount={3}
                          widths={["100%", "70%", "40%"]}
                          heights={["10px", "10px", "10px"]}
                          spacing={2}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            </Container>
          </Box>
        ) : null}

        {!isLoading && !passes.length && !LANDING_PAGE_SETTINGS?.HIDE_PASSES ? (
          <Container maxWidth={"lg"}>
            <Stack py={4} alignItems={"center"}>
              <EmptyState
                title="No passes available"
                description="Please check back later for more information."
              />
            </Stack>
          </Container>
        ) : null}

        {!isLoading && passes.length ? (
          <React.Fragment>
            {!LANDING_PAGE_SETTINGS?.HIDE_PASSES ? (
              <Box width={"100%"}>
                <PassBanner
                  pass={passes[0]}
                  passDisplayMode={
                    LANDING_PAGE_SETTINGS?.PASS_BANNER?.PASS_DISPLAY_MODE
                  }
                  hidePassDetails={
                    LANDING_PAGE_SETTINGS?.PASS_BANNER?.HIDE_PASS_DETAILS
                  }
                  passCardProps={{
                    showFAQButton: LANDING_PAGE_SETTINGS?.PASS?.SHOW_FAQ_BUTTON,
                    hideSubscribeButton:
                      Boolean(passes[0]?.isGiftEligible) &&
                      TEAM_GLOBAL_SETTINGS?.DISABLE_STANDARD_PURCHASE_ON_GIFT_PASSES,
                    onClickFAQButton,
                  }}
                />
              </Box>
            ) : null}

            {!LANDING_PAGE_SETTINGS?.HIDE_PASSES &&
            LANDING_PAGE_SETTINGS?.SHOW_ALL_PASSES_UNDER_BANNER ? (
              <Box width={"100%"}>
                <Container>
                  <PassCardList
                    passes={passes}
                    passCardProps={{
                      variant: "variant1",
                      showViewScheduleButton: true,
                      showFAQButton:
                        LANDING_PAGE_SETTINGS?.PASS?.SHOW_FAQ_BUTTON,
                      onClickFAQButton,
                    }}
                  />
                </Container>
              </Box>
            ) : null}

            {!LANDING_PAGE_SETTINGS?.HIDE_UPCOMING_EVENTS ? (
              <Box>
                <Container maxWidth="lg">
                  <UpcomingEvents
                    events={getUpcomingEventsToDisplay(passes, teamConfig)}
                    title={
                      LANDING_PAGE_SETTINGS?.UPCOMING_EVENTS_TITLE ||
                      "Events Included"
                    }
                    showAllEvents={
                      LANDING_PAGE_SETTINGS?.SHOW_ALL_EVENTS_UNDER_PASS
                    }
                    orderEventsByDateAsc={
                      LANDING_PAGE_SETTINGS?.ORDER_EVENTS_CHRONOLOGICALLY
                    }
                  />
                </Container>
              </Box>
            ) : null}
          </React.Fragment>
        ) : null}

        {!TEAM_GLOBAL_SETTINGS?.DISABLE_SIT_WITH_FRIENDS ? (
          <Box width={"100%"}>
            <Container maxWidth="lg">
              <SWAFPrompt />
            </Container>
          </Box>
        ) : null}

        {!isLoading &&
        !urlCode &&
        passes.length > 0 &&
        !LANDING_PAGE_SETTINGS?.SHOW_ALL_PASSES_UNDER_BANNER &&
        !LANDING_PAGE_SETTINGS?.HIDE_PASSES ? (
          <Box>
            <Container>
              <PassCardList
                passes={passes.filter((_, idx) => idx !== 0)}
                title={"Subscription Plans"}
                passCardProps={{
                  variant: "variant2",
                }}
              />
            </Container>
          </Box>
        ) : null}
      </Stack>

      <Box py={2} />
    </PageLayout>
  );
}

export const getServerSideProps = (async (ctx) => {
  const { req } = ctx;
  // x-bp-uid is present on the initial navigation request from the MLB Ballpark App.
  // On subsequent client-side navigation requests (/_next/data/...) it won't be in
  // the headers, so we fall back to the cookie set by index.tsx.
  const { oktaId, email } = constants.mlbBallparkHeaders;
  const mlbBallparkOktaId =
    (req?.headers?.[oktaId] as string | null) ??
    (req?.cookies?.[oktaId] as string | null);
  const mlbBallparkEmail =
    (req?.headers?.[email] as string | null) ??
    (req?.cookies?.[email] as string | null) ??
    null;

  if (!mlbBallparkOktaId)
    return {
      props: {
        mlbBallparkOktaId: null,
        mlbBallparkEmail: null,
      },
    };

  return { props: { mlbBallparkOktaId, mlbBallparkEmail } };
}) satisfies GetServerSideProps<TMLBBallparkUser>;
